/**
 * Project: Aniatek
 * Author: Carlos A. Loaiza | Connect & Launch
 * Tel: +52 (667) 247 - 6316
 * Intellectual Property: All rights reserved (c) 2026
 */

<template>
    <div class="configuracion-electrica">

        <div v-for="(inv, index) in inversores" :key="index" class="inversor-card">
            <div class="card-header">
                <strong>Inversor {{ index + 1 }}:</strong> {{ inv.modelo }}
                <span class="badge-paneles">{{ inv.cantidadPaneles }} paneles</span>
            </div>

            <div class="card-body">
                <div class="input-row">
                    <div class="form-group">
                        <label>Distancia DC (Paneles a Inversor)</label>
                        <div class="input-suffix">
                            <input type="number" v-model.number="inv.distanciaDC" @input="recalcular(index)" min="0">
                            <span>m</span>
                        </div>
                    </div>

                    <div class="form-group">
                        <label>Distancia AC (Inversor a Tablero)</label>
                        <div class="input-suffix">
                            <input type="number" v-model.number="inv.distanciaAC" @input="recalcular(index)" min="0">
                            <span>m</span>
                        </div>
                    </div>
                </div>

                <div class="mini-stats" v-if="inv.calculos">
                    <div class="stat-group">
                        <strong>Lado DC:</strong>
                        <small>{{ inv.calculos.tramosDC }} tramos tubería</small>
                        <small>{{ inv.calculos.metrosDC }}m cable (x2)</small>
                    </div>
                    <div class="stat-divider"></div>
                    <div class="stat-group">
                        <strong>Lado AC:</strong>
                        <small>{{ inv.calculos.tramosAC }} tramos tubería</small>
                        <small>{{ inv.calculos.metrosAC }}m cable (x3)</small>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const CATALOGO_CABLES = [
    { calibre: '12', amps: 25, areaTierra: 9.08, areaSolar: 19.64 },
    { calibre: '10', amps: 35, areaTierra: 14.53, areaSolar: 28.28 },
    { calibre: '8', amps: 50, areaTierra: 25.52, areaSolar: 50.90 },
];

const CAPACIDAD_TUBERIA = [
    { nombre: '3/4"', limite: 151 },
    { nombre: '1"', limite: 248 },
    { nombre: '1 1/4"', limite: 425 },
    { nombre: '1 1/2"', limite: 573 },
    { nombre: '2"', limite: 937 }
];
const CATALOGO_AC_THHW = [
    { calibre: '14', amps: 20, area: 6.61 },
    { calibre: '12', amps: 25, area: 9.08 },
    { calibre: '10', amps: 35, area: 14.53 },
    { calibre: '8', amps: 50, area: 25.52 },
    { calibre: '6', amps: 65, area: 35.26 },
    { calibre: '4', amps: 85, area: 56.75 },
    { calibre: '2', amps: 115, area: 80.12 },
    { calibre: '1', amps: 130, area: 105.69 },
    { calibre: '1/0', amps: 150, area: 126.68 },
    { calibre: '2/0', amps: 175, area: 151.75 },
    { calibre: '3/0', amps: 200, area: 181.46 },
    { calibre: '4/0', amps: 230, area: 219.05 },
    { calibre: '250', amps: 255, area: 268.81 },
    { calibre: '300', amps: 285, area: 311.03 },
    { calibre: '350', amps: 310, area: 356.33 }
];
const BREAKERS_AC = [
    15, 20, 30, 40, 50, 60, 70, 100,
    125, 150, 175, 200, 225, 250, 300,
    400, 500, 600, 700, 800, 1000
];
const MIN_TUBO_POR_CALIBRE = {
    '12': '1/2"',
    '10': '1/2"',
    '8': '3/4"', 
    '6': '1"',
    '4': '1 1/4"',
    '2': '1 1/4"',
    '1/0': '2"',
    '2/0': '2"',
    '4/0': '2 1/2"'
}
export default {
    name: 'ConfiguracionElectrica',
    props: {
        inversoresIniciales: { type: Array, required: true, default: () => [] },
        catalogoMateriales: { type: Array, default: () => [] }
    },
    data() {
        return {
            inversores: [],
            listaMaterialesExtras: [],
        }
    },
    watch: {
        inversoresIniciales: {
            immediate: true,
            deep: true,
            handler(newVal) {
                if (!newVal) return;
                if (JSON.stringify(newVal) === JSON.stringify(this.inversores)) return;

                this.inversores = newVal.map((inv, i) => {
                    const existente = this.inversores[i];
                    const localEsDefaultDC = existente && parseFloat(existente.distanciaDC) === 15;
                    const localEsDefaultAC = existente && parseFloat(existente.distanciaAC) === 10;

                    let distDC = (inv.distanciaDC ?? 15);
                    if (existente && !localEsDefaultDC) {
                        distDC = existente.distanciaDC;
                    }

                    let distAC = (inv.distanciaAC ?? 10);
                    if (existente && !localEsDefaultAC) {
                        distAC = existente.distanciaAC;
                    }

                    return {
                        ...inv,
                        distanciaDC: parseFloat(distDC),
                        distanciaAC: parseFloat(distAC),
                        calculos: existente ? existente.calculos : { tramosDC: 0, tramosAC: 0, metrosDC: 0, metrosAC: 0 }
                    };
                });

                this.$nextTick(() => {
                    this.inversores.forEach((_, i) => this.recalcular(i));
                });
            }
        },
        catalogoMateriales: {
            deep: true,
            handler() { this.inversores.forEach((_, i) => this.recalcular(i)); }
        }
    },
    methods: {
        getTuberiaDC(numStrings, iscPanel) {
            const corrienteMax = iscPanel * 1.25;
            const ampacidadA = corrienteMax * 1.25;
            const ampacidadB = corrienteMax / (0.82 * 0.70);
            const ampacidadRequeridaPV = Math.max(ampacidadA, ampacidadB);

            let cablePV = CATALOGO_CABLES.find(c => c.amps >= ampacidadRequeridaPV);
            if (!cablePV) cablePV = CATALOGO_CABLES[CATALOGO_CABLES.length - 1];

            const ampacidadBaseProteccion = corrienteMax * 1.25;
            let proteccionSeleccionada = 15;
            const pasosProteccion = [15, 20, 25, 30, 35, 40, 45, 50, 60, 70, 80, 90, 100];

            for (let cap of pasosProteccion) {
                if (ampacidadBaseProteccion <= cap) {
                    proteccionSeleccionada = cap;
                    break;
                }
                proteccionSeleccionada = cap;
            }

            let calibreTierraStr = '8'; 

            if (proteccionSeleccionada <= 20) {
                calibreTierraStr = '12';
            } else if (proteccionSeleccionada <= 60) {
                calibreTierraStr = '10';
            } else if (proteccionSeleccionada <= 100) {
                calibreTierraStr = '8';
            }

            const cableTierraDatos = CATALOGO_CABLES.find(c => c.calibre === calibreTierraStr);

            const areaSolarTotal = (numStrings * 2) * cablePV.areaSolar;

            const areaTierraTotal = 1 * cableTierraDatos.areaTierra;

            const ocupacionTotal = areaSolarTotal + areaTierraTotal;

            const tubo = CAPACIDAD_TUBERIA.find(t => t.limite >= ocupacionTotal);
            const medidaFinal = tubo ? tubo.nombre : '2"';

            return {
                tuberia: medidaFinal,
                calibreTierra: calibreTierraStr 
            };
        },

        getTuberiaAC(potenciaW, voltaje = 220, corrienteBD = 0, fasesInv = '1') {

            const fasesStr = (fasesInv || '').toString().toLowerCase();
            const esTrifasico = fasesStr === '2' || fasesStr.includes('tri') || fasesStr.includes('3');
            const factorFase = esTrifasico ? 1.732 : 1;

            let corrienteNominal = corrienteBD > 0 ? corrienteBD : (potenciaW / (voltaje * factorFase));

            const ampacidadBreaker = corrienteNominal * 1.25;
            let breakerSeleccionado = BREAKERS_AC.find(b => b >= ampacidadBreaker);
            if (!breakerSeleccionado) breakerSeleccionado = BREAKERS_AC[BREAKERS_AC.length - 1];

            let calibreTierra = '8';
            if (breakerSeleccionado <= 20) calibreTierra = '12';
            else if (breakerSeleccionado <= 60) calibreTierra = '10';
            else if (breakerSeleccionado <= 100) calibreTierra = '8';
            else if (breakerSeleccionado <= 200) calibreTierra = '6';
            else if (breakerSeleccionado <= 300) calibreTierra = '4';
            else if (breakerSeleccionado <= 500) calibreTierra = '2';
            else calibreTierra = '1/0';

            const ampsRequeridosCable = ampacidadBreaker / 0.82;

            let cableFase = CATALOGO_AC_THHW.find(c => c.amps >= ampsRequeridosCable);

            if (!cableFase) cableFase = CATALOGO_AC_THHW[CATALOGO_AC_THHW.length - 1];

            const numHilosVivos = esTrifasico ? 4 : 2;
            const datosTierra = CATALOGO_AC_THHW.find(c => c.calibre === calibreTierra) || CATALOGO_AC_THHW[2];

            const ocupacion = (numHilosVivos * cableFase.area) + (1 * datosTierra.area);
            const tuboPorAreaObj = CAPACIDAD_TUBERIA.find(t => t.limite >= ocupacion);
            const tuboPorArea = tuboPorAreaObj ? tuboPorAreaObj.nombre : '2"';

            const tuboPorDiametro = MIN_TUBO_POR_CALIBRE[cableFase.calibre] || '1/2"';

            const indexArea = CAPACIDAD_TUBERIA.findIndex(t => t.nombre === tuboPorArea);
            const indexDiam = CAPACIDAD_TUBERIA.findIndex(t => t.nombre === tuboPorDiametro);
            const indexFinal = Math.max(indexArea, indexDiam);
            const medidaFinal = indexFinal >= 0 ? CAPACIDAD_TUBERIA[indexFinal].nombre : '2"';

            return {
                tuberia: medidaFinal,
                calibreFase: cableFase.calibre,
                calibreTierra: calibreTierra,
                breaker: breakerSeleccionado,
                numHilos: numHilosVivos
            };
        },
        recalcular(index) {
            const inv = this.inversores[index];
            if (!inv) return;

            const potencia = parseFloat(inv.potenciaW || 0); 
            const paneles = parseInt(inv.cantidadPaneles || 0);

            let divisor = 9; 
            if (potencia > 20000) {
                divisor = 15;
            }

            const stringsCalculados = Math.ceil(paneles / divisor);

            inv.numStrings = stringsCalculados;

            const mDC = parseFloat(inv.distanciaDC);
            const mAC = parseFloat(inv.distanciaAC);
            const LONGITUD_TRAMO = 3.0; 

            inv.calculos = {
                metrosDC: mDC,
                metrosAC: mAC,
                tramosDC: Math.ceil(mDC / LONGITUD_TRAMO),
                tramosAC: Math.ceil(mAC / LONGITUD_TRAMO)
            };

            this.emitirDatosTabla();
        },

        emitirDatosTabla() {
            if (!this.catalogoMateriales || this.catalogoMateriales.length === 0) return;

            let consDC = {};
            let consAC = {};
            let consExtras = {};
            const buscarDatos = (nombreBase, medida = '') => {
                if (!nombreBase) return { precio: 0, unidad: 'Pieza', nombreReal: 'N/A' };

                const limpiar = (txt) => txt ? txt.toString().toLowerCase().trim() : '';
                const baseBuscada = limpiar(nombreBase);
                const medidaBuscada = limpiar(medida);

                let candidatos = this.catalogoMateriales.filter(item =>
                    limpiar(item.Articulo).includes(baseBuscada)
                );

                let encontrado = null;
                if (medidaBuscada && candidatos.length > 0) {
                    encontrado = candidatos.find(item => {
                        const articuloLimpio = limpiar(item.Articulo);
                        return articuloLimpio.includes(` ${medidaBuscada} `) ||
                            articuloLimpio.endsWith(` ${medidaBuscada}`) ||
                            articuloLimpio === medidaBuscada;
                    });

                    if (!encontrado) {
                        encontrado = candidatos.find(item => limpiar(item.Articulo).includes(medidaBuscada));
                    }
                }

                if (!encontrado && candidatos.length > 0) {
                    encontrado = candidatos[0];
                }

                if (encontrado) {
                    const precioFinal = parseFloat(encontrado.Precio || encontrado.Costo || 0);
                    return { precio: precioFinal, unidad: encontrado.Unidad, nombreReal: encontrado.Articulo };
                }

                return { precio: 0, unidad: 'Pieza', nombreReal: `${nombreBase} ${medida}`.trim() };
            };

            const agregar = (diccionario, nombreBase, cantidad, medida = '') => {
                if (cantidad <= 0) return;
                const datos = buscarDatos(nombreBase, medida);
                const key = datos.nombreReal; 

                if (!diccionario[key]) {
                    diccionario[key] = { cant: 0, cost: datos.precio, unit: datos.unidad };
                }
                diccionario[key].cant += cantidad;
            };

            const getGabineteSize = (numTuberias) => {
                if (numTuberias <= 2) return '30 cm x 25 cm';
                if (numTuberias <= 6) return '40 cm x 30 cm';
                if (numTuberias <= 8) return '50 cm x 40 cm';
                if (numTuberias <= 12) return '70 cm x 50 cm';
                if (numTuberias <= 16) return '80 cm x 60 cm';
                return '100 cm x 80 cm';

            };
            this.inversores.forEach(inv => {
                const c = inv.calculos;
                const numTuberias = inv.numTuberias || 1;
                const largoTubo = 3.1;
                const hayPaneles = (inv.cantidadPaneles || 0) > 0;

                const iscPanel = parseFloat(inv.isc || 13.5);
                const numStrings = inv.numStrings || Math.ceil(inv.cantidadPaneles / 9) || 1;
                const numTuberiasFinal = numStrings > 4 ? Math.ceil(numStrings / 4) : 1;

                const resultadoCalculo = this.getTuberiaDC(numStrings, iscPanel);

                const MEDIDA_DC = resultadoCalculo.tuberia; 
                const CALIBRE_TIERRA = resultadoCalculo.calibreTierra;

                if (c.metrosDC > 0 && hayPaneles) {
                    const distDC = c.metrosDC;
                    const consuletT = numStrings - 1;

                    const calculoBaseTubos = Math.ceil((distDC / 3.1)) + Math.ceil((numStrings * 0.5));
                    const cantTubosDC = Math.ceil(calculoBaseTubos) * numTuberias;
                    const cantConduletsDC = Math.ceil(distDC / 4) + 1 + numStrings;

                    const gabineterp = numStrings <= 2 ? 1 : 2
                    const cantConectoresDC = ((cantConduletsDC + gabineterp) * 2) + (numStrings + (consuletT * 3));
                    const cantUnicanalDC = Math.ceil(((cantTubosDC * 3.1) + 1.2) * 0.1);
                    const cantAbrazaderasDC = cantUnicanalDC / 0.1;
                    const cantTornillosDC = cantAbrazaderasDC + (gabineterp * 4);

                    agregar(consDC, 'Tubería pared delgada metálica galvanizada', cantTubosDC * numTuberiasFinal, MEDIDA_DC);
                    agregar(consDC, 'Condulet LB, LR, LL', cantConduletsDC * numTuberiasFinal, MEDIDA_DC);
                    agregar(consDC, 'Conectores WP', cantConectoresDC * numTuberiasFinal, MEDIDA_DC);
                    agregar(consDC, 'Coples WP', Math.max(0, cantTubosDC - 1) * numTuberiasFinal, MEDIDA_DC);
                    agregar(consDC, 'Abrazadera de unicanal', cantAbrazaderasDC * numTuberiasFinal, MEDIDA_DC);

                    const sizeGabinete = getGabineteSize(numStrings);
                    agregar(consDC, 'Gabinete de protecciones metálico', gabineterp * numTuberiasFinal, sizeGabinete);
                    agregar(consDC, 'Unicanal 4 x 2 cm', cantUnicanalDC * numTuberiasFinal);
                    agregar(consDC, 'Taquete expansivo plástico', cantTornillosDC * numTuberiasFinal, '3/8');
                    agregar(consDC, 'Pija punta fina', cantTornillosDC * numTuberiasFinal, '3/8');
                    agregar(consDC, 'Pija punta de broca', cantTornillosDC * numTuberiasFinal, '3/8');

                    const metrosTierraDC = Math.ceil(distDC * numStrings * 1.20) * numTuberias;

                    agregar(consDC, 'Cable THHW Calibre ', metrosTierraDC * numTuberiasFinal, CALIBRE_TIERRA);

                    agregar(consDC, 'Cople roscado', numStrings * numTuberiasFinal, MEDIDA_DC);
                    agregar(consDC, 'Conector glándula', (numStrings + 1) * numTuberiasFinal, MEDIDA_DC);
                    agregar(consDC, 'Condulet T', consuletT * numTuberiasFinal, MEDIDA_DC);

                    agregar(consExtras, 'Cable Fotovolcaico', Math.ceil((distDC * 2 * numStrings) * 1.2) * numTuberiasFinal);
                    agregar(consExtras, 'ITM CD de 2 POLOS', numStrings * numTuberiasFinal, 'DC');
                    agregar(consExtras, 'Conectores MC4(Par)', numStrings * numTuberiasFinal, 'Par');

                }

                if (c.metrosAC > 0 && hayPaneles) {
                    const distAC = c.metrosAC;

                    const potenciaInv = parseFloat(inv.potenciaW || 0);
                    const corrienteInv = parseFloat(inv.corrienteAC || 0);
                    const fasesInv = inv.fases || 'bifasica';

                    const calculoAC = this.getTuberiaAC(potenciaInv, 220, corrienteInv, fasesInv);

                    const MEDIDA_AC = calculoAC.tuberia;
                    const CALIBRE_FASE = calculoAC.calibreFase;     
                    const CALIBRE_TIERRA = calculoAC.calibreTierra; 
                    const BREAKER_AC = calculoAC.breaker;
                    const HILOS_AC = calculoAC.numHilos;

                    const cantTubosAC = Math.ceil(distAC / largoTubo) * numTuberias;
                    const cantUnicanalAC = Math.ceil(((cantTubosAC * 3.1) + 1.2) * 0.1);
                    const cantAbrazaderasAC = cantUnicanalAC * 10;
                    const cantConduletsAC = Math.ceil(distAC / 4);
                    const cantConectoresAC = ((cantConduletsAC + 1) * 2) + Math.ceil(distAC / 5);
                    const cantTornillosAC = cantAbrazaderasAC + 4;

                    agregar(consAC, 'Tubería pared delgada metálica galvanizada', cantTubosAC, MEDIDA_AC);
                    agregar(consAC, 'Condulet LB, LR, LL', cantConduletsAC, MEDIDA_AC);
                    agregar(consAC, 'Conectores WP', cantConectoresAC, MEDIDA_AC);
                    agregar(consAC, 'Coples WP', Math.max(0, cantTubosAC - 1), MEDIDA_AC);
                    agregar(consAC, 'Abrazadera de unicanal', cantAbrazaderasAC, MEDIDA_AC);

                    agregar(consAC, 'Gabinete de protecciones metálico 30 cm x 25 cm', 1);
                    agregar(consAC, 'Unicanal 4 x 2 cm', cantUnicanalAC);
                    agregar(consAC, 'Taquete expansivo plástico', cantTornillosAC, '3/8');
                    agregar(consAC, 'Pija punta fina', cantTornillosAC, '3/8');
                    agregar(consAC, 'Pija punta de broca', cantTornillosAC, '3/8');

                    const metrosFases = Math.ceil(distAC * HILOS_AC * 1.20) * numTuberias;
                    const metrosTierraAC = Math.ceil(distAC * 1 * 1.20) * numTuberias;

                    agregar(consAC, 'Cable THHW', metrosFases, CALIBRE_FASE);

                    agregar(consAC, 'Cable THHW', metrosTierraAC, CALIBRE_TIERRA);

                    agregar(consAC, 'ITM', 1, '2P ' + BREAKER_AC.toString() + 'A');

                }
            });
        
            const mapList = (dict) => Object.keys(dict).map(k => ({
                articulo: k, 
                unidad: dict[k].unit,
                cantidad: Math.ceil(dict[k].cant),
                costo: dict[k].cost,
                total: Math.ceil(dict[k].cant) * dict[k].cost
            }));

            const listaDC = mapList(consDC);
            const listaAC = mapList(consAC);

            const listaExtras = mapList(consExtras);

            this.listaMaterialesExtras = listaExtras;
            const total = listaDC.reduce((s, i) => s + i.total, 0) + listaAC.reduce((s, i) => s + i.total, 0) + listaExtras.reduce((s, i) => s + i.total, 0);
            const configAGuardar = this.inversores.map(inv => ({
                distanciaDC: inv.distanciaDC,
                distanciaAC: inv.distanciaAC
            }));

            this.$emit('update-costos', { total, listaDC, listaAC, listaExtras, configuracionInversores: configAGuardar });
        }
    }
}
</script>

<style scoped>
.configuracion-electrica {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.inversor-card {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 15px;
}

.card-header {
    font-size: 14px;
    color: #333;
    margin-bottom: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.badge-paneles {
    background: #e3f2fd;
    color: #1565c0;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: bold;
}

.input-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 15px;
}

.form-group label {
    display: block;
    font-size: 12px;
    color: #666;
    margin-bottom: 4px;
}

.input-suffix {
    display: flex;
    align-items: center;
    background: #fff;
    border: 1px solid #ced4da;
    border-radius: 4px;
    padding: 0 8px;
}

.input-suffix input {
    border: none;
    width: 100%;
    text-align: right;
    padding: 6px;
    outline: none;
    font-size: 13px;
}

.input-suffix span {
    color: #888;
    font-size: 12px;
    margin-left: 4px;
}

.mini-stats {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px dashed #ddd;
    display: flex;
    justify-content: space-around;
    align-items: center;
    font-size: 11px;
    color: #666;
}

.stat-group {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.stat-divider {
    width: 1px;
    height: 25px;
    background-color: #ddd;
}
</style>